import React, { useEffect, useState, useMemo, useContext, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  IconButton,
  Heading,
  Select,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";

import { ImFilter } from "react-icons/im";
import { TbSettings } from "react-icons/tb";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { AuthContext } from "../../providers/AuthValidationLayer";
import { fetchRoles } from "../../actions/role.actions.js";
import { Unauthorized } from "../../components/Unauthorized";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { Loading } from "../../components/Loading";

export default function ManageRoleUI() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const paginationRef = useRef(null);
  const { hasPermission } = useContext(AuthContext);
  const canViewRoles = hasPermission("Access Control", "Roles", "GET");

  const [search, setSearch] = useState({
    roleName: "",
    roleDescription: "",
    createdAt: "",
    assignedAdmins: "",
  });

  if (!canViewRoles) {
    return <Unauthorized />;
  }

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    setLoadError(null);
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setLoadError("Request timed out. Please try again.");
    }, 15000);

    try {
      const data = await fetchRoles();
      setRoles(data || []);
    } catch (error) {
      setRoles([]);
      setLoadError(error.message || "Failed to load roles");
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  // Filtering logic
  const filteredRoles = useMemo(() => {
    return roles.filter((role) => {
      const formattedDate = new Date(role.createdAt).toLocaleDateString(
        "en-GB",
      );

      return (
        role.roleName.toLowerCase().includes(search.roleName.toLowerCase()) &&
        (role.roleDescription || "")
          .toLowerCase()
          .includes(search.roleDescription.toLowerCase()) &&
        (search.createdAt === "" || formattedDate.includes(search.createdAt)) &&
        String(role.assignedAdmins || 0)
          .toLowerCase()
          .includes(search.assignedAdmins.toLowerCase())
      );
    });
  }, [roles, search]);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredRoles]);

  // Pagination logic
  const totalPages = Math.ceil(filteredRoles.length / pageSize);
  const paginatedRoles = filteredRoles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const handleRowsPerPageChange = (e) => {
    const nextPageSize = Number(e.target.value);
    const previousTop = paginationRef.current?.getBoundingClientRect().top;

    setPageSize(nextPageSize);
    setCurrentPage(1);

    if (typeof previousTop === "number") {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const nextTop = paginationRef.current?.getBoundingClientRect().top;
          if (typeof nextTop === "number") {
            const delta = nextTop - previousTop;
            if (Math.abs(delta) > 1) {
              window.scrollBy({ top: delta, behavior: "auto" });
            }
          }
        });
      });
    }
  };

  return (
    <Box bg="#F5F7FA" minH="100vh" p={6}>
      {/* BREADCRUMB */}
      <BreadCrumbMaker
        items={[
          { title: "Access Control", link: "/settings" },
          { title: "Manage Roles", link: "/settings/roles" },
        ]}
      />

      {/* HEADER */}
      <Flex justify="space-between" align="center" mt={2} mb={4}>
        <Text fontSize="2xl" fontWeight="700">
          Roles List
        </Text>

        <Button
          as={Link}
          to="/settings/roles/create?source=manage-roles"
          bg="#4C7CF7"
          color="white"
          px={7}
          h="45px"
          borderRadius="10px"
          _hover={{ bg: "#3b6ae0" }}
        >
          + Create New Role
        </Button>
      </Flex>

      {/* TABLE */}
      <Box
        bg="white"
        borderRadius="16px"
        border="1px solid #E5E7EB"
        display="flex"
        flexDirection="column"
        minH="520px"
      >
        <Box overflow="hidden" borderTopRadius="16px" flex="1" display="flex" flexDirection="column">
          {/* SEARCH FILTERS */}
          <Flex
            bg="#E9EEFF"
            px={4}
            py={3}
            gap={4}
            borderBottom="1px solid #E4E7EC"
          >
            <Input
              size="sm"
              placeholder="Search Role Name"
              bg="white"
              borderRadius="8px"
              onChange={(e) =>
                setSearch((s) => ({ ...s, roleName: e.target.value }))
              }
            />
            <Input
              size="sm"
              placeholder="Search Description"
              bg="white"
              borderRadius="8px"
              onChange={(e) =>
                setSearch((s) => ({ ...s, roleDescription: e.target.value }))
              }
            />
          </Flex>

          {loading ? (
            <Box py={10}>
              <Loading />
            </Box>
          ) : (
            <Box flex="1" overflowX="auto">
              <Table variant="simple">
                <Thead bg="#F9FAFB">
                  <Tr>
                    <Th w="80px">Sr.No</Th>
                    <Th>Role Name</Th>
                    <Th>Description</Th>
                    <Th w="150px">Created On</Th>
                    <Th w="120px" textAlign="center">
                      Assigned Admins
                    </Th>
                  </Tr>
                </Thead>

                <Tbody>
                  {paginatedRoles.length === 0 ? (
                    <Tr>
                      <Td colSpan="5" textAlign="center" py={8} color="gray.500">
                        {loadError || "No roles found"}
                      </Td>
                    </Tr>
                  ) : (
                    paginatedRoles.map((role, index) => (
                      <Tr
                        key={role._id}
                        _hover={{ bg: "#F0F4FF", cursor: "pointer" }}
                        borderBottom="1px solid #F0F2F5"
                        onClick={() =>
                          navigate(`/settings/roles/view/${role._id}`)
                        }
                      >
                        <Td>
                          {String(
                            (currentPage - 1) * pageSize + index + 1,
                          ).padStart(2, "0")}
                          .
                        </Td>
                        <Td fontWeight="500" maxW="200px">
                          <Flex align="center" gap={2}>
                            <Text isTruncated maxW="150px" title={role.roleName}>
                              {role.roleName}
                            </Text>
                            {role.roleName.toLowerCase() === "superadmin" && (
                              <Badge colorScheme="red" fontSize="xs">
                                System
                              </Badge>
                            )}
                          </Flex>
                        </Td>
                        <Td color="gray.600" maxW="300px">
                          <Text isTruncated title={role.roleDescription}>
                            {role.roleDescription || "—"}
                          </Text>
                        </Td>
                        <Td>
                          {new Date(role.createdAt).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </Td>
                        <Td textAlign="center">
                          <Badge colorScheme="blue" fontSize="sm">
                            {role.assignedAdmins || 0}
                          </Badge>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>

        {/* PAGINATION */}
        <Flex
          ref={paginationRef}
          justify="space-between"
          align="center"
          p={4}
          borderTop="1px solid #E5E7EB"
          mt="auto"
          bg="white"
          position="sticky"
          bottom="0"
          zIndex={2}
        >
          <Flex align="center" gap={2}>
            <Text fontSize="sm">Page</Text>
            <Text fontWeight="600">{currentPage}</Text>
            <Text fontSize="sm">of {totalPages}</Text>
          </Flex>

          <Flex align="center" gap={2}>
            <Text fontSize="sm">Rows per page:</Text>
            <Select
              size="sm"
              width="80px"
              value={pageSize}
              onChange={handleRowsPerPageChange}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </Select>
          </Flex>

          <Flex gap={2}>
            <IconButton
              size="sm"
              onClick={prevPage}
              isDisabled={currentPage === 1}
              icon={<ChevronLeftIcon />}
              variant="outline"
            />
            <IconButton
              size="sm"
              onClick={nextPage}
              isDisabled={currentPage === totalPages}
              icon={<ChevronRightIcon />}
              variant="outline"
            />
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
}
